import { useEffect, useState } from "react";
import { plateLabel, roman, sigil, type Plate } from "../shared/plates";
import { api } from "./api";
import { withText } from "./png";
import { Credit } from "./trust";

/** Plates on the client: the sigil, the tag under each chapter image, saving a plate as a PNG, and the album page. */

export function Sigil({ code, size = 28, className = "" }: { code: string; size?: number; className?: string }) {
  const { points, chords, mark } = sigil(code);
  return (
    <svg className={`lm-sigil ${className}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="3" opacity=".45" />
      {chords.map(([a, b]) => (
        <line key={`${a}-${b}`} x1={points[a]![0]} y1={points[a]![1]} x2={points[b]![0]} y2={points[b]![1]} stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      ))}
      <circle cx={points[mark]![0]} cy={points[mark]![1]} r="8" fill="currentColor" />
    </svg>
  );
}

export interface PlateInfo { i: number; code: string; caption: string; drawnAt?: number }

/** Under a chapter image: sigil, "Plate II · 7F2A-91C3", and save. */
export function PlateTag({ src, plate, className = "" }: { src: string; plate: PlateInfo; className?: string }) {
  const [state, setState] = useState<"idle" | "saving" | "failed">("idle");
  const save = () => {
    setState("saving");
    savePlate(src, plate).then(() => setState("idle"), () => setState("failed"));
  };
  return (
    <div className={`lm-plate-tag ${className}`}>
      <Sigil code={plate.code} />
      <span className="lm-plate-code">{plateLabel(plate.i, plate.code)}</span>
      <button type="button" onClick={save} disabled={state === "saving"} aria-label={`Save plate ${roman(plate.i + 1)} as an image`}>
        {state === "saving" ? "saving…" : state === "failed" ? "couldn't save, try again" : "save"}
      </button>
    </div>
  );
}

// ---- saving: the picture on a paper mount with its sigil, number, caption and code; the code is also in the file ------

const fmtDate = (t?: number) => (t ? new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

/** Draws the plate (image + mount) and downloads it as weftwake-plate-<code>.png, with the code in the PNG's text fields. */
export async function savePlate(src: string, p: PlateInfo): Promise<void> {
  const img = new Image();
  img.src = src;
  await img.decode();
  const W = img.naturalWidth || 1024, H = img.naturalHeight || 1024;
  const pad = Math.round(W * 0.05), band = Math.round(W * 0.19);
  const canvas = document.createElement("canvas");
  canvas.width = W + pad * 2;
  canvas.height = H + pad + band;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "#f2efe8";
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(img, pad, pad, W, H);

  // The sigil, drawn like the SVG one.
  const s = sigil(p.code), size = band * 0.56, x0 = pad, y0 = pad + H + (band - size) / 2;
  const at = (q: [number, number]): [number, number] => [x0 + (q[0] / 100) * size, y0 + (q[1] / 100) * size];
  g.strokeStyle = g.fillStyle = "#151515";
  g.lineCap = "round";
  g.globalAlpha = 0.45; g.lineWidth = size * 0.03;
  g.beginPath(); g.arc(x0 + size / 2, y0 + size / 2, size * 0.46, 0, Math.PI * 2); g.stroke();
  g.globalAlpha = 1; g.lineWidth = size * 0.05;
  for (const [a, b] of s.chords) { g.beginPath(); g.moveTo(...at(s.points[a]!)); g.lineTo(...at(s.points[b]!)); g.stroke(); }
  g.beginPath(); g.arc(...at(s.points[s.mark]!), size * 0.08, 0, Math.PI * 2); g.fill();

  // Number and code, caption, date.
  const tx = x0 + size + pad * 0.6, maxW = canvas.width - tx - pad;
  const unit = W / 1024;
  g.textBaseline = "alphabetic";
  g.font = `${Math.round(22 * unit)}px ui-monospace, "SF Mono", Menlo, monospace`;
  g.fillStyle = "#555";
  g.fillText(`WEFTWAKE · PLATE ${roman(p.i + 1)} · ${p.code}`, tx, y0 + size * 0.22, maxW);
  g.font = `italic ${Math.round(34 * unit)}px Georgia, "Times New Roman", serif`;
  g.fillStyle = "#151515";
  const lines = wrapText(g, p.caption, maxW).slice(0, 2);
  lines.forEach((l, k) => g.fillText(l, tx, y0 + size * 0.58 + k * 40 * unit, maxW));
  g.font = `${Math.round(20 * unit)}px ui-monospace, "SF Mono", Menlo, monospace`;
  g.fillStyle = "#777";
  g.fillText(`AI-generated · drawn by the loom${p.drawnAt ? `, ${fmtDate(p.drawnAt)}` : ""}`, tx, y0 + size * 1.02, maxW);

  const blob = await new Promise<Blob>((ok, no) => canvas.toBlob((b) => (b ? ok(b) : no(new Error("toBlob"))), "image/png"));
  const tagged = await withText(blob, {
    Title: `Weftwake plate ${roman(p.i + 1)} · ${p.code}`,
    Description: p.caption,
    Source: "Weftwake",
    Comment: `weftwake-plate:v1:${p.code}`,
    // EU AI Act Art. 50: machine-readable marking that the image is AI-generated (IPTC digital source type).
    "Digital Source Type": "http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia",
    Disclaimer: "AI-generated image. A keepsake from Weftwake.",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(tagged);
  a.download = `weftwake-plate-${p.code}.png`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

function wrapText(g: CanvasRenderingContext2D, text: string, width: number): string[] {
  const out: string[] = [];
  let line = "";
  for (const w of text.split(/\s+/)) {
    const next = line ? `${line} ${w}` : w;
    if (g.measureText(next).width > width && line) { out.push(line); line = w; } else line = next;
  }
  if (line) out.push(line);
  return out;
}

// ---- the album -------------------------------------------------------------------------------------

export function Album({ goHome, open }: { goHome(): void; open(story: string): void }) {
  const [plates, setPlates] = useState<Plate[] | null>(null);
  const [failed, setFailed] = useState(false);
  const load = () => { setFailed(false); api.plates().then((r) => setPlates(r.plates), () => setFailed(true)); };
  useEffect(load, []);
  return (
    <main className="lm-album">
      <header>
        <a href="/" onClick={(e) => { e.preventDefault(); goHome(); }}>← back to your stories</a>
        <h1>Your plates</h1>
        <p>Each night the loom dreams over your stories and draws one plate for the chapter. Every plate is one of a kind: its code and sigil come from your story, that moment in it and the picture itself.</p>
        <p className="lm-gate-small">Plates are AI-generated keepsakes. Weftwake doesn't sell plates or run a market for them.</p>
      </header>
      {failed && <p role="alert">Couldn't fetch your plates. <button type="button" onClick={load}>Try again</button></p>}
      {!plates && !failed && <p aria-live="polite">Gathering your plates…</p>}
      {plates?.length === 0 && <p>No plates yet. Play a story tonight; the loom draws its first plate while you sleep, and it will hang here in the morning.</p>}
      {!!plates?.length && (
        <ul className="lm-album-grid">
          {plates.map((p) => {
            const src = api.imageUrl(p.story, p.i);
            return (
              <li key={`${p.story}:${p.i}`}>
                <figure className="lm-plate">
                  <img src={src} alt={p.caption} loading="lazy" />
                  <figcaption>{p.caption}</figcaption>
                </figure>
                <PlateTag src={src} plate={p} />
                <a className="lm-album-story" href={`/s/${p.story}`} onClick={(e) => { e.preventDefault(); open(p.story); }}>
                  from “{p.title}” · {fmtDate(p.drawnAt)}
                </a>
              </li>
            );
          })}
        </ul>
      )}
      <Credit />
    </main>
  );
}
